import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Grid,
  Chip,
  LinearProgress,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Swal from "sweetalert2";
import { getRequest, postRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL } from "Includes/urls";

function MyClientsList(props) {
  const [activeTab, setActiveTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [params, setParams] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [paramData, setParamData] = useState({}); // { paramId: { is_using: true, reason: '' } }
  const [updatesByCompany, setUpdatesByCompany] = useState({});

  // System report state
  const [systemReportData, setSystemReportData] = useState({});
  const [systemReportColumns, setSystemReportColumns] = React.useState([]);
  const [systemReportLoading, setSystemReportLoading] = React.useState(false);
  const [expandedCompanyId, setExpandedCompanyId] = React.useState(null);
  const [systemReportTabs, setSystemReportTabs] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState('');

  useEffect(() => {
    fetchData();
    fetchParams();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem("user");
      let currentUserId = null;
      if (userStr) {
        currentUserId = JSON.parse(userStr).id;
      }

      const [res, groupsRes, updatesRes] = await Promise.all([
        getRequest(GET_URL.company.api, { limit: 10000 }, props).catch(() => null),
        getRequest(GET_URL.client_groups.api, { limit: 10000 }, props).catch(() => null),
        getRequest(GET_URL.client_track_company_updates.api, { limit: 10000 }, props).catch(() => null)
      ]);
      
      let allowedCompanyIds = new Set();

      if (groupsRes && groupsRes.data) {
        const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data.data || []);
        groups.forEach(group => {
          if (group.group_leader === currentUserId) {
            if (group.companies_details) {
              group.companies_details.forEach(company => {
                allowedCompanyIds.add(company.id);
              });
            }
          }
        });
      }

      if (updatesRes && updatesRes.data) {
        const updatesList = Array.isArray(updatesRes.data) ? updatesRes.data : (updatesRes.data.data || []);
        const updatesMap = {};
        updatesList.forEach(update => {
          if (!updatesMap[update.company]) {
            const paramMap = {};
            update.param_updates?.forEach(p => {
              paramMap[p.param] = p;
            });
            updatesMap[update.company] = paramMap;
          }
        });
        setUpdatesByCompany(updatesMap);
      }

      if (res && res.data) {
        let allCompanies = Array.isArray(res.data) ? res.data : (res.data.data || []);
        if (currentUserId) {
          allCompanies = allCompanies.filter(c =>
            c.coordinator_id === currentUserId || allowedCompanyIds.has(c.id)
          );
        }
        setRows(allCompanies);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const fetchParams = async () => {
    try {
      const res = await getRequest(GET_URL.client_track_params.api, {}, props);
      if (res && res.data) {
        setParams(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Fetch system report data for all assigned companies
  const fetchSystemReport = React.useCallback(async () => {
    if (!rows.length) return;
    setSystemReportLoading(true);
    try {
      // Fetch the full client track report data
      const res = await getRequest(
        GET_URL.client_track_report.api,
        { limit: 10000 },
        props
      );
      if (res?.status === 200) {
        const reportRows = res.data?.data || [];
        const columns = res.data?.columns || [];
        setSystemReportColumns(columns);
        setSystemReportTabs(res.data?.tabs || []);

        // Group report data by institute name, matching against company names
        const dataByCompany = {};
        const companyNameSet = new Set(rows.map((c) => (c.name || '').trim().toLowerCase()));

        reportRows.forEach((report) => {
          const instName = (report.institute_name || '').trim().toLowerCase();
          if (companyNameSet.has(instName)) {
            if (!dataByCompany[instName]) {
              dataByCompany[instName] = [];
            }
            dataByCompany[instName].push(report);
          }
        });

        setSystemReportData(dataByCompany);
      }
    } catch (e) {
      console.error(e);
    }
    setSystemReportLoading(false);
  }, [rows, props]);

  useEffect(() => {
    if (activeTab === 1 && rows.length > 0) {
      fetchSystemReport();
    }
  }, [activeTab, rows.length, fetchSystemReport]);

  const handleStatusOpen = (company) => {
    setSelectedCompany(company);
    const initialData = {};
    params.forEach(p => {
      initialData[p.id] = { is_using: true, reason: "" };
    });
    setParamData(initialData);
    setStatusOpen(true);
  };

  const handleStatusClose = () => {
    setStatusOpen(false);
    setSelectedCompany(null);
  };

  const handleParamToggle = (paramId, checked) => {
    setParamData(prev => ({
      ...prev,
      [paramId]: { ...prev[paramId], is_using: checked }
    }));
  };

  const handleReasonChange = (paramId, text) => {
    setParamData(prev => ({
      ...prev,
      [paramId]: { ...prev[paramId], reason: text }
    }));
  };

  const handleSubmit = async () => {
    // validation
    for (const p of params) {
      const data = paramData[p.id];
      if (!data.is_using && !data.reason.trim()) {
        Swal.fire("Error", `Please provide a reason for not using "${p.name}" module`, "error");
        return;
      }
    }

    const payload = {
      company: selectedCompany.id,
      param_updates: params.map(p => ({
        param: p.id,
        is_using: paramData[p.id].is_using,
        reason: paramData[p.id].is_using ? "" : paramData[p.id].reason
      }))
    };

    setLoading(true);
    try {
      await postRequest(POST_URL.client_track_company_updates.api, payload, props);
      Swal.fire("Success", "Status updated successfully", "success");
      handleStatusClose();
      fetchData(); // Refresh the table
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Something went wrong", "error");
    }
    setLoading(false);
  };

  const columns = [
    { field: "name", headerName: "Company Name", flex: 2 },
    { field: "code", headerName: "Code", flex: 1 },
    {
      field: "coordinator_name",
      headerName: "Coordinator",
      flex: 1.5,
      renderCell: (paramsObj) => paramsObj.row.coordinator_name || '-',
    },
    ...params.map(p => ({
      field: `param_${p.id}`,
      headerName: p.name,
      flex: 1.5,
      renderCell: (paramsObj) => {
        const companyId = paramsObj.row.id;
        const updateMap = updatesByCompany[companyId];
        if (!updateMap || !updateMap[p.id]) return "-";
        const paramData = updateMap[p.id];
        if (paramData.is_using) return "Using";
        return `Not Using (${paramData.reason || "No reason"})`;
      }
    })),
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (paramsObj) => (
        <Box>
          <Button size="small" variant="outlined" onClick={() => handleStatusOpen(paramsObj.row)}>
            Add Status
          </Button>
        </Box>
      ),
    },
  ];

  // Summary columns for system report view
  const SUMMARY_KEYS = [
    'fee_collection_last_used_value',
    'attendance_used_percentage',
    'diary_used_percentage',
    'last_exam_used_percentage',
    'exam_marks_used_percentage',
    'institute_poc',
  ];

  const displayColumns = React.useMemo(() => {
    if (!systemReportColumns.length) return [];
    return systemReportColumns.filter(
      (col) => SUMMARY_KEYS.includes(col.schemacolumn)
    );
  }, [systemReportColumns]);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">My Clients</Typography>
      </Box>

      <Paper sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            px: 2,
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
          }}
        >
          <Tab label="Manual Report" />
          <Tab label="System Report" />
        </Tabs>
      </Paper>

      {/* Tab 0: Manual Report (current functionality) */}
      {activeTab === 0 && (
        <Paper sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Paper>
      )}

      {/* Tab 1: System Report (data from ClientTrackReport) */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <TextField
              select
              size="small"
              label="Academic Year"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Years (Latest)</MenuItem>
              {systemReportTabs.map((tab) => (
                <MenuItem key={tab} value={tab}>{tab}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {systemReportLoading && <LinearProgress />}
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Coordinator</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Academic Year</TableCell>
                  {displayColumns.map((col) => (
                    <TableCell key={col.schemacolumn} sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                      {col.column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !systemReportLoading ? (
                  <TableRow>
                    <TableCell colSpan={5 + displayColumns.length}>
                      <Typography align="center" sx={{ py: 4 }}>No companies assigned.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((company) => {
                    const companyKey = (company.name || '').trim().toLowerCase();
                    const allReports = systemReportData[companyKey] || [];
                    const reports = selectedAcademicYear
                      ? allReports.filter((r) => r.tab_name === selectedAcademicYear)
                      : allReports;
                    const latestReport = reports.length > 0 ? reports[0] : null;
                    const isExpanded = expandedCompanyId === company.id;

                    return (
                      <React.Fragment key={company.id}>
                        <TableRow hover>
                          <TableCell>
                            {reports.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => setExpandedCompanyId(isExpanded ? null : company.id)}
                              >
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{company.name}</TableCell>
                          <TableCell>{company.code || '-'}</TableCell>
                          <TableCell>{company.coordinator_name || '-'}</TableCell>
                          <TableCell>{latestReport?.tab_name || '-'}</TableCell>
                          {displayColumns.map((col) => {
                            const val = latestReport?.[col.schemacolumn];
                            return (
                              <TableCell key={col.schemacolumn}>
                                {val === 'Never Used' ? (
                                  <Chip label="Never Used" size="small" color="error" variant="outlined" />
                                ) : val ? (
                                  val
                                ) : reports.length === 0 ? (
                                  <Typography variant="caption" color="text.secondary">No data</Typography>
                                ) : '-'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {reports.length > 1 && (
                          <TableRow>
                            <TableCell sx={{ py: 0 }} colSpan={5 + displayColumns.length}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: '#fbfdff' }}>
                                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                    All Academic Year Reports
                                  </Typography>
                                  {reports.map((report, idx) => (
                                    <Paper
                                      key={report.id || idx}
                                      variant="outlined"
                                      sx={{ p: 1.5, mb: 1, borderRadius: 2 }}
                                    >
                                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                        {report.tab_name || 'Unknown Year'}
                                      </Typography>
                                      <Grid container spacing={1}>
                                        {systemReportColumns.map((col) => {
                                          const val = report[col.schemacolumn];
                                          if (!val && val !== 0) return null;
                                          return (
                                            <Grid item xs={6} md={3} key={col.schemacolumn}>
                                              <Typography variant="caption" color="text.secondary">
                                                {col.column}
                                              </Typography>
                                              <Typography variant="body2" fontWeight={500}>
                                                {val === 'Never Used' ? (
                                                  <Chip label="Never Used" size="small" color="error" variant="outlined" />
                                                ) : val}
                                              </Typography>
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </Paper>
                                  ))}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </Box>
      )}

      <Dialog open={statusOpen} onClose={handleStatusClose} fullWidth maxWidth="sm">
        <DialogTitle>Add Status for {selectedCompany?.name}</DialogTitle>
        <DialogContent dividers>
          {params.length === 0 ? (
            <Typography>No modules configured yet.</Typography>
          ) : (
            params.map(p => {
              const data = paramData[p.id] || { is_using: true, reason: "" };
              return (
                <Box key={p.id} mb={3} p={2} border={1} borderColor="grey.300" borderRadius={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="bold">{p.name}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={data.is_using}
                          onChange={(e) => handleParamToggle(p.id, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={data.is_using ? "Using" : "Not Using"}
                    />
                  </Box>
                  {!data.is_using && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Reason for not using"
                      value={data.reason}
                      onChange={(e) => handleReasonChange(p.id, e.target.value)}
                      margin="dense"
                      required
                      error={!data.reason.trim()}
                      helperText={!data.reason.trim() ? "Reason is required" : ""}
                    />
                  )}
                </Box>
              )
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || params.length === 0}>
            Submit Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyClientsList;
